/**
 * GPX / track parsing + stats for the trail-maps tool.
 *
 * Every consumer app we care about (Garmin, Strava, Komoot, AllTrails, Apple
 * Fitness) exports GPX, so a single browser-side parser covers all of them.
 * One uploaded file == one "segment" (a day of a multi-day hike). The tool
 * holds an ordered array of segments; render + stats just iterate.
 *
 * No dependencies — uses the browser DOMParser. d3-geo handles projection
 * downstream (see TrailMapRender), not here.
 */

const EARTH_R = 6371000; // metres

/** Great-circle distance in metres between two {lat, lon} points. */
export function haversine(a, b) {
    const toRad = (d) => (d * Math.PI) / 180;
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    return 2 * EARTH_R * Math.asin(Math.sqrt(h));
}

const numText = (parent, tag) => {
    const el = parent.getElementsByTagName(tag)[0];
    if (!el) return null;
    const v = parseFloat(el.textContent);
    return Number.isFinite(v) ? v : null;
};

const timeText = (parent) => {
    const el = parent.getElementsByTagName('time')[0];
    if (!el) return null;
    const t = Date.parse(el.textContent);
    return Number.isFinite(t) ? t : null;
};

/**
 * Parse a GPX string into a single track segment.
 *   { name, points: [{ lat, lon, ele, time }] }
 * Prefers <trkpt>; falls back to <rtept> (route exports) then <wpt>.
 * Throws on malformed XML or a track with no usable points.
 */
export function parseGpx(text, fallbackName = 'Track') {
    const doc = new DOMParser().parseFromString(text, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) {
        throw new Error('That file is not valid GPX.');
    }

    let nodes = Array.from(doc.getElementsByTagName('trkpt'));
    if (!nodes.length) nodes = Array.from(doc.getElementsByTagName('rtept'));
    if (!nodes.length) nodes = Array.from(doc.getElementsByTagName('wpt'));

    const points = nodes
        .map((n) => ({
            lat: parseFloat(n.getAttribute('lat')),
            lon: parseFloat(n.getAttribute('lon')),
            ele: numText(n, 'ele'),
            time: timeText(n),
        }))
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));

    if (points.length < 2) {
        throw new Error('No track points found in that file.');
    }

    const nameEl =
        doc.querySelector('trk > name') ||
        doc.querySelector('metadata > name') ||
        doc.querySelector('rte > name');
    const name = (nameEl && nameEl.textContent.trim()) || fallbackName;

    return { name, points };
}

/** Moving-average smoothing over elevations to tame GPS noise before we sum
 *  elevation gain — raw per-point deltas wildly overstate it otherwise. */
function smoothElevations(points, window = 5) {
    const ele = points.map((p) => p.ele);
    if (ele.some((e) => e == null)) return null; // no elevation in this track
    const out = new Array(ele.length);
    const half = Math.floor(window / 2);
    for (let i = 0; i < ele.length; i++) {
        let sum = 0;
        let n = 0;
        for (let j = Math.max(0, i - half); j <= Math.min(ele.length - 1, i + half); j++) {
            sum += ele[j];
            n++;
        }
        out[i] = sum / n;
    }
    return out;
}

/** Per-segment stats. distanceM is always present; elevation/duration are
 *  null when the source track lacks <ele>/<time>. */
export function trackStats(points) {
    let distanceM = 0;
    for (let i = 1; i < points.length; i++) {
        distanceM += haversine(points[i - 1], points[i]);
    }

    const smooth = smoothElevations(points);
    let gainM = null;
    let lossM = null;
    let eleMin = null;
    let eleMax = null;
    if (smooth) {
        gainM = 0;
        lossM = 0;
        eleMin = Infinity;
        eleMax = -Infinity;
        for (let i = 0; i < smooth.length; i++) {
            eleMin = Math.min(eleMin, smooth[i]);
            eleMax = Math.max(eleMax, smooth[i]);
            if (i > 0) {
                const d = smooth[i] - smooth[i - 1];
                if (d > 0) gainM += d;
                else lossM -= d;
            }
        }
    }

    const times = points.map((p) => p.time).filter((t) => t != null);
    const durationS =
        times.length >= 2 ? (times[times.length - 1] - times[0]) / 1000 : null;

    return { distanceM, gainM, lossM, eleMin, eleMax, durationS };
}

/** Aggregate stats across an ordered list of segments (the multi-day total). */
export function aggregateStats(segments) {
    const per = segments.map((s) => trackStats(s.points));
    const sum = (key) =>
        per.reduce((acc, s) => (s[key] == null ? acc : acc + s[key]), 0);
    const anyEle = per.some((s) => s.gainM != null);
    const anyTime = per.some((s) => s.durationS != null);
    const eleMins = per.map((s) => s.eleMin).filter((v) => v != null);
    const eleMaxs = per.map((s) => s.eleMax).filter((v) => v != null);
    return {
        days: segments.length,
        distanceM: sum('distanceM'),
        gainM: anyEle ? sum('gainM') : null,
        lossM: anyEle ? sum('lossM') : null,
        durationS: anyTime ? sum('durationS') : null,
        eleMin: eleMins.length ? Math.min(...eleMins) : null,
        eleMax: eleMaxs.length ? Math.max(...eleMaxs) : null,
        perSegment: per,
    };
}

/** GeoJSON FeatureCollection of LineStrings — fed to d3-geo fitExtent so the
 *  projection frames every segment together. */
export function segmentsToGeoJson(segments) {
    return {
        type: 'FeatureCollection',
        features: segments.map((s) => ({
            type: 'Feature',
            properties: { name: s.name },
            geometry: {
                type: 'LineString',
                coordinates: s.points.map((p) => [p.lon, p.lat]),
            },
        })),
    };
}

/** Concatenated elevation profile across all segments: cumulative distance
 *  (m) vs elevation (m), with the index of each day boundary. Null if no
 *  segment carries elevation. */
export function elevationProfile(segments) {
    const pts = [];
    const dayBreaks = [];
    let cumDist = 0;
    let hasEle = false;
    segments.forEach((seg) => {
        dayBreaks.push(pts.length);
        seg.points.forEach((p, i) => {
            if (i > 0) cumDist += haversine(seg.points[i - 1], p);
            if (p.ele != null) hasEle = true;
            pts.push({ dist: cumDist, ele: p.ele });
        });
    });
    if (!hasEle) return null;
    return { points: pts, dayBreaks, totalDist: cumDist };
}

/* ---- formatting helpers (UI only) ---- */
export const fmtKm = (m) => `${(m / 1000).toFixed(1)} km`;
export const fmtMi = (m) => `${(m / 1609.34).toFixed(1)} mi`;
export const fmtM = (m) => `${Math.round(m)} m`;
export const fmtFt = (m) => `${Math.round(m * 3.28084)} ft`;
export const fmtDuration = (s) => {
    if (s == null) return '—';
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return h ? `${h}h ${m}m` : `${m}m`;
};

/** Unit-aware formatters. units = 'metric' | 'imperial'. */
export const formatDistance = (m, units) => (units === 'imperial' ? fmtMi(m) : fmtKm(m));
export const formatElevation = (m, units) => (units === 'imperial' ? fmtFt(m) : fmtM(m));

/** Bounding-box aspect (width / height) of the combined track in metres
 *  (longitude scaled by latitude). >1 = wider than tall, <1 = taller. */
export function trailAspect(segments) {
    const pts = segments.flatMap((s) => s.points);
    const lats = pts.map((p) => p.lat);
    const lons = pts.map((p) => p.lon);
    const meanLat = (Math.min(...lats) + Math.max(...lats)) / 2;
    const wM = (Math.max(...lons) - Math.min(...lons)) * Math.cos((meanLat * Math.PI) / 180) * 111320;
    const hM = (Math.max(...lats) - Math.min(...lats)) * 111320;
    if (wM <= 0 || hM <= 0) return 1;
    return wM / hM;
}

/** Map-area aspect derived from the trail shape, clamped so a long ridge or
 *  out-and-back doesn't produce an unprintable sliver. */
export function mapAreaAspect(segments, { min = 0.7, max = 1.5 } = {}) {
    return Math.max(min, Math.min(max, trailAspect(segments)));
}
